/**
 * Open Food Facts API client
 *
 * Docs: https://openfoodfacts.github.io/openfoodfacts-server/api/
 *
 * All requests are made directly from the browser – OFF is a public API
 * with permissive CORS headers and no API key requirement.
 */

const OFF_BASE = "https://world.openfoodfacts.net/api/v2";

/**
 * Fields we request from OFF to keep responses lean.
 * We include both `_100g` variants (preferred) and bare names as fallback
 * because product completeness varies widely.
 */
const FIELDS = [
  "code",
  "product_name",
  "brands",
  "quantity",
  "nutrition_grades",
  "nutriments",
  "ingredients_text",
].join(",");

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OFFNutriments {
  "energy-kcal_100g"?: number;
  "energy-kj_100g"?: number;
  proteins_100g?: number;
  carbohydrates_100g?: number;
  sugars_100g?: number;
  fat_100g?: number;
  "saturated-fat_100g"?: number;
  "trans-fat_100g"?: number;
  "monounsaturated-fat_100g"?: number;
  "polyunsaturated-fat_100g"?: number;
  fiber_100g?: number;
  salt_100g?: number;
  sodium_100g?: number;
  cholesterol_100g?: number;
  calcium_100g?: number;
  water_100g?: number;
  // bare names (some products omit _100g suffix)
  "energy-kcal"?: number;
  "energy-kj"?: number;
  proteins?: number;
  carbohydrates?: number;
  sugars?: number;
  fat?: number;
  "saturated-fat"?: number;
  "trans-fat"?: number;
  "monounsaturated-fat"?: number;
  "polyunsaturated-fat"?: number;
  fiber?: number;
  salt?: number;
  sodium?: number;
  cholesterol?: number;
  calcium?: number;
  water?: number;
}

export interface OFFProduct {
  code: string;
  product_name?: string;
  brands?: string;
  quantity?: string;
  nutrition_grades?: string;
  nutriments?: OFFNutriments;
  ingredients_text?: string;
}

export interface OFFSearchResult {
  count: number;
  products: OFFProduct[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Pick the first defined numeric value from a list of candidates.
 * Returns undefined when none are available.
 */
function pick(...values: (number | undefined)[]): number | undefined {
  for (const v of values) {
    if (v !== undefined && v !== null && !isNaN(v)) return v;
  }
  return undefined;
}

/**
 * Format a number for use in FitDennik's string-based numeric fields.
 * Returns undefined when the value is absent.
 */
function fmt(v: number | undefined): string | undefined {
  if (v === undefined) return undefined;
  // Round to 2 decimal places and strip trailing zeros
  return parseFloat(v.toFixed(2)).toString();
}

/**
 * Try to extract a gram value from a quantity string returned by OFF.
 *
 * OFF quantity strings are free-form, e.g.:
 *   "500g", "500 g", "1.5kg", "1,5 kg", "500g (2x250g)", "500 g e",
 *   "6 x 100 g", "2x500g", "100g / 3.5oz"
 *
 * We extract the first numeric+unit token that uses a weight unit (g/kg).
 * Volume units (ml, l, cl) are intentionally ignored because they do not
 * map reliably to grams without density information.
 *
 * Returns undefined when no weight quantity can be determined.
 */
export function parseQuantityToGrams(quantity?: string): string | undefined {
  if (!quantity) return undefined;
  const lower = quantity.toLowerCase();

  // Handle multiplier patterns like "6 x 100 g" or "2x500g" → total grams
  const multiMatch = lower.match(/([\d.,]+)\s*[x×]\s*([\d.,]+)\s*(g|kg)/);
  if (multiMatch) {
    const count = parseFloat(multiMatch[1].replace(",", "."));
    const unit = parseFloat(multiMatch[2].replace(",", "."));
    if (!isNaN(count) && !isNaN(unit)) {
      const grams = multiMatch[3] === "kg" ? count * unit * 1000 : count * unit;
      return fmt(grams);
    }
  }

  // Match the first occurrence of a number followed by g or kg
  // Tolerates trailing text like "500g e" or "500g (net)"
  const match = lower.match(/([\d.,]+)\s*(kg|g)(?:[^a-z]|$)/);
  if (!match) return undefined;

  const value = parseFloat(match[1].replace(",", "."));
  if (isNaN(value)) return undefined;

  const grams = match[2] === "kg" ? value * 1000 : value;
  return fmt(grams);
}

// ─── Nutrient parser ─────────────────────────────────────────────────────────

/**
 * Partial FitDennik food record populated from an OFF product.
 * All fields are strings (matching the tRPC input schema) or undefined.
 */
export interface ParsedOFFProduct {
  nazov: string;
  barcode: string;
  energiaKcal?: string;
  energiaKj?: string;
  proteinG?: string;
  sacharidG?: string;
  cukorG?: string;
  tukG?: string;
  nmkG?: string;
  transG?: string;
  monoG?: string;
  polyG?: string;
  vlakninaG?: string;
  solG?: string;
  sodikG?: string;
  cholesterolMg?: string;
  vapnikMg?: string;
  vodaG?: string;
  balenieG?: string;
  poznamka?: string;
}

export function parseOFFProduct(product: OFFProduct): ParsedOFFProduct {
  const n = product.nutriments ?? {};

  // Sodium / salt: prefer explicit sodium_100g; derive from salt if missing
  // Relationship: sodium = salt / 2.5
  const sodiumRaw =
    pick(n.sodium_100g, n.sodium) ??
    (pick(n.salt_100g, n.salt) !== undefined
      ? (pick(n.salt_100g, n.salt) as number) / 2.5
      : undefined);

  const saltRaw = pick(n.salt_100g, n.salt);

  // Cholesterol: OFF stores it in g/100g, FitDennik wants mg/100g
  const cholesterolRaw = pick(n.cholesterol_100g, n.cholesterol);
  const cholesterolMg =
    cholesterolRaw !== undefined ? cholesterolRaw * 1000 : undefined;

  // Calcium: OFF stores it in g/100g, FitDennik wants mg/100g
  const calciumRaw = pick(n.calcium_100g, n.calcium);
  const calciumMg = calciumRaw !== undefined ? calciumRaw * 1000 : undefined;

  // Build a short note with brand + nutrition grade if available
  const noteParts: string[] = [];
  if (product.brands) noteParts.push(product.brands);
  if (product.nutrition_grades)
    noteParts.push(`Nutri-Score: ${product.nutrition_grades.toUpperCase()}`);
  noteParts.push(`OFF barcode: ${product.code}`);
  const poznamka = noteParts.join(" | ");

  return {
    nazov: product.product_name?.trim() || "",
    barcode: product.code,
    energiaKcal: fmt(pick(n["energy-kcal_100g"], n["energy-kcal"])),
    energiaKj: fmt(pick(n["energy-kj_100g"], n["energy-kj"])),
    proteinG: fmt(pick(n.proteins_100g, n.proteins)),
    sacharidG: fmt(pick(n.carbohydrates_100g, n.carbohydrates)),
    cukorG: fmt(pick(n.sugars_100g, n.sugars)),
    tukG: fmt(pick(n.fat_100g, n.fat)),
    nmkG: fmt(pick(n["saturated-fat_100g"], n["saturated-fat"])),
    transG: fmt(pick(n["trans-fat_100g"], n["trans-fat"])),
    monoG: fmt(pick(n["monounsaturated-fat_100g"], n["monounsaturated-fat"])),
    polyG: fmt(pick(n["polyunsaturated-fat_100g"], n["polyunsaturated-fat"])),
    vlakninaG: fmt(pick(n.fiber_100g, n.fiber)),
    solG: fmt(saltRaw),
    sodikG: fmt(sodiumRaw),
    cholesterolMg: fmt(cholesterolMg),
    vapnikMg: fmt(calciumMg),
    vodaG: fmt(pick(n.water_100g, n.water)),
    balenieG: parseQuantityToGrams(product.quantity),
    poznamka,
  };
}

// ─── API calls ────────────────────────────────────────────────────────────────

/**
 * Fetch a single product by barcode (EAN-13, EAN-8, UPC-A, etc.).
 * Returns null when the product is not found.
 */
export async function fetchProductByBarcode(
  barcode: string
): Promise<OFFProduct | null> {
  const url = `${OFF_BASE}/product/${encodeURIComponent(barcode)}?fields=${FIELDS}`;
  const res = await fetch(url, {
    headers: { "User-Agent": "FitDennik/1.0 (https://fitdennik.sk)" },
  });

  if (!res.ok) return null;

  const json = await res.json();
  if (json.status !== 1 || !json.product) return null;

  return { code: barcode, ...json.product } as OFFProduct;
}

/**
 * Search products by name.
 * Returns up to `pageSize` results.
 */
export async function searchProductsByName(
  query: string,
  pageSize = 20
): Promise<OFFProduct[]> {
  if (!query.trim()) return [];

  const params = new URLSearchParams({
    search_terms: query.trim(),
    fields: FIELDS,
    page_size: String(pageSize),
    sort_by: "unique_scans_n", // most-scanned products first
  });

  const url = `${OFF_BASE}/search?${params.toString()}`;
  const res = await fetch(url, {
    headers: { "User-Agent": "FitDennik/1.0 (https://fitdennik.sk)" },
  });

  if (!res.ok) return [];

  const json: OFFSearchResult = await res.json();
  return json.products ?? [];
}
